/**
 * Owner tools (Idea #82; ADR 0098): the worker's jobs, started now. The owner
 * asked for them apart from Health, hidden by default: they aren't in the
 * main menu. The backtick key opens them over the page the owner is on
 * (OwnerToolsKey; #512, ADR 0134), and so does Owner tools in the user menu,
 * under the theme (#661). `/tools` shows them as a page.
 *
 * Each tool queues a job the worker already runs on its schedule, and says
 * where its result shows once it has run: Health's own fields, or the day
 * itself. A second click while the same job is still waiting is told so;
 * nothing is queued twice. The API decides who may, and what.
 *
 * The tools are grouped by integration (ADR 0141), as Health's cards are:
 * each panel lists its tools in one order, look back, copy, check, then
 * subscriptions.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useId, useState } from "react";
import { Link } from "react-router";

import { ApiError } from "../../api/client";
import type {
  BackupCheck,
  BackupRequest,
  EmailPreviewKind,
  Lane,
  ListedBackup,
  OwnerJob,
  OwnerJobKey,
  OwnerJobRun,
  Queued,
  UserSummary,
} from "../../api/types";
import { useSession } from "../../app/session-context";
import { Combobox, type ComboboxOption } from "../../components/ui/Combobox";
import { ChevronRightIcon } from "../../components/ui/Icons";
import { Panel } from "../../components/ui/Panel";
import { formatBytes } from "../../lib/bytes";
import { formatDateTimeIn, formatLongDay, formatTimeIn, isIsoDay, todayIn } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { possessive } from "../../lib/possessive";
import { usePrivacy } from "../../lib/privacy";

import { CaptureTools } from "./Captures";

const BACKUP_REQUEST_KEY = ["owner-tools", "backup"];

const primaryButton =
  "h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg " +
  "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";

const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

const linkClass = "font-semibold text-brand-ink hover:underline";

// The periods offered, in hours: Read CDRs keeps about a week, and
// ScreenConnect's read-back goes as far (ADR 0098).
const PERIODS: readonly ComboboxOption[] = [
  { value: "1", label: "1 hour" },
  { value: "4", label: "4 hours" },
  { value: "24", label: "24 hours" },
  { value: "72", label: "3 days" },
  { value: "168", label: "7 days" },
];
const DEFAULT_PERIOD = "24";
// Datto RMM's: the same periods, up to a week (ADR 0139), 1 hour by default
// (datto-rmm.md; ADR 0135).
const DATTO_PERIODS = PERIODS;

const OWNER_JOBS_KEY = ["owner-tools", "jobs"];

const secondaryButton =
  "h-10 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold " +
  "hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

function userName(user: UserSummary): string {
  return user.display_name ?? user.email ?? "Unnamed user";
}

// The tools' headings: under the page's h1, or under the popup's h2.
const ToolHeadingLevel = createContext<2 | 3>(2);

/**
 * One integration's panel (#512; ADR 0134, 0141): it opens collapsed to one
 * row, its name, as Health's cards do (ADR 0119), so every row is the same
 * height. The row is a button that shows the rest: its tools.
 */
function ToolPanel({
  title,
  description,
  children,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
}) {
  const level = useContext(ToolHeadingLevel);
  const Heading = level === 2 ? "h2" : "h3";
  const headingId = useId();
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  return (
    <Panel aria-labelledby={headingId} className="flex min-w-0 flex-col gap-3">
      <Heading className="text-lg font-semibold tracking-tight">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          className={
            "flex w-full cursor-pointer items-center gap-2 rounded-row text-left " +
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink"
          }
          onClick={() => {
            setOpen(!open);
          }}
        >
          <span
            className={
              "flex text-muted transition-transform motion-reduce:transition-none " + (open ? "rotate-90" : "")
            }
          >
            <ChevronRightIcon />
          </span>
          <span id={headingId}>{title}</span>
        </button>
      </Heading>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-3">
          <p className="text-[13.5px] text-muted">{description}</p>
          {children}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * One tool inside an integration's panel: its name, what it does, and its
 * fields. The panel is the integration's (the owner, 2026-10-10: one place
 * for each vendor's tools, as Health has one card for each).
 */
function ToolSection({ title, description, children }: { title: string; description: ReactNode; children: ReactNode }) {
  const level = useContext(ToolHeadingLevel);
  const Heading = level === 2 ? "h3" : "h4";
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-2 border-t border-line pt-3 first:border-t-0 first:pt-0"
    >
      <Heading id={headingId} className="text-[15px] font-semibold">
        {title}
      </Heading>
      <p className="text-[13px] text-muted">{description}</p>
      {children}
    </section>
  );
}

/** What came of the last click: queued, already waiting, or refused. */
function Outcome({
  queued,
  error,
  waiting,
  where,
}: {
  queued: Pick<Queued, "queued" | "asked_at"> | undefined;
  error: string | null;
  // Said when the same job was already waiting.
  waiting: string;
  // Where its result shows once it has run.
  where: ReactNode;
}) {
  const { me } = useSession();
  if (error !== null) {
    return (
      <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
        {error}
      </p>
    );
  }
  if (queued === undefined) {
    return null;
  }
  return (
    <p role="status" className="rounded-row bg-panel-2 px-3 py-2.5 text-[13.5px]">
      {queued.queued ? `Queued at ${formatTimeIn(queued.asked_at, me.timezone)}. ` : `${waiting} `}
      {where}
    </p>
  );
}

/** A tool's request: the last answer, or why it failed. */
function useTool<T, R extends Pick<Queued, "queued" | "asked_at"> = Queued>(
  run: (input: T) => Promise<R>,
  onAnswered?: () => void,
) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: run,
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      onAnswered?.();
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  return {
    ask: (input: T) => {
      mutation.mutate(input);
    },
    pending: mutation.isPending,
    queued: error === null ? mutation.data : undefined,
    error,
    setError,
  };
}

function HealthLink() {
  return (
    <Link to="/health" className={linkClass}>
      Open Health
    </Link>
  );
}

function AutotaskSync() {
  const { api } = useSession();
  const tool = useTool(() => api.syncAutotaskNow());
  return (
    <ToolSection
      title="Autotask sync now"
      description="The nightly sync (07:00 UTC) of Autotask's companies, contacts, resources and choices, now: for a change in Autotask the app should see before tonight."
    >
      <div>
        <button
          type="button"
          className={primaryButton}
          disabled={tool.pending}
          onClick={() => {
            tool.ask(undefined);
          }}
        >
          Sync Autotask now
        </button>
      </div>
      <Outcome
        queued={tool.queued}
        error={tool.error}
        waiting="A sync is already waiting to run, so nothing more was queued."
        where={
          <>
            Once it has run, Health → Autotask shows it under Last successful sync. <HealthLink />
          </>
        }
      />
    </ToolSection>
  );
}

// Axon Time's emails, each sent to the owner who asks alone (the owner,
// 2026-10-14: "Me, as a preview").
const EMAILS: readonly ComboboxOption[] = [
  { value: "test", label: "Test email" },
  { value: "reminder", label: "Morning reminder" },
  { value: "summary", label: "Weekly summary" },
  { value: "alert", label: "Health alert" },
];

function SendEmail() {
  const { api, me } = useSession();
  const [kind, setKind] = useState<EmailPreviewKind>("test");
  const [userId, setUserId] = useState(me.id);
  const forSomeone = kind === "reminder" || kind === "summary";
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    staleTime: 5 * 60_000,
    enabled: forSomeone,
  });
  const tool = useTool((input: { kind: EmailPreviewKind; userId: string | undefined }) =>
    api.previewEmail(input.kind, input.userId),
  );
  const people: ComboboxOption[] = (users.data ?? [])
    .filter((user) => user.is_active)
    .map((user) => ({ value: user.id, label: userName(user), detail: user.id === me.id ? "you" : undefined }));

  return (
    <ToolSection
      title="Send an email"
      description="Sends you, and only you, one of Axon Time's emails now: the test email; a morning reminder or weekly summary as the person you choose would get it today, from their real work; or the alert Health would bring now. Each but the test is marked a preview, and nothing else changes: the 8:30 AM run and the alerts send their own. While EMAIL_SEND_MODE is off, nothing is queued, and it says so."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (forSomeone && userId === "") {
            tool.setError("Choose whose email to preview.");
            return;
          }
          tool.ask({ kind, userId: forSomeone ? userId : undefined });
        }}
      >
        <Combobox
          label="Email"
          options={[...EMAILS]}
          value={kind}
          onChange={(value) => {
            setKind(value as EmailPreviewKind);
          }}
          placeholder="Choose an email"
        />
        {forSomeone ? (
          <Combobox
            label="As"
            options={people}
            value={userId}
            onChange={setUserId}
            loading={users.isPending}
            failed={users.isError}
            placeholder="Type a name"
            failedText="Couldn't load the people"
          />
        ) : null}
        <div>
          <button type="submit" className={primaryButton} disabled={tool.pending}>
            Send it to me
          </button>
        </div>
      </form>
      <Outcome
        queued={tool.queued}
        error={tool.error}
        waiting="One of your emails is already waiting to be sent, so nothing more was queued."
        where="Within a minute or so it's in your inbox; Health → Microsoft 365 shows it as the last email sent. If there's nothing to send (nothing waiting, a quiet week, every card fine), it says so. One that hasn't started within 10 minutes (the worker down, or mid-deploy) isn't sent at all: ask again."
      />
    </ToolSection>
  );
}

function RebuildDay() {
  const { api, me } = useSession();
  const dayId = useId();
  const [userId, setUserId] = useState("");
  const [day, setDay] = useState(() => todayIn(me.timezone));
  // Once the owner picks a day, choosing a person keeps it.
  const [dayPicked, setDayPicked] = useState(false);
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    staleTime: 5 * 60_000,
  });
  const tool = useTool((input: { userId: string; day: string }) => api.rebuildDay(input.userId, input.day));
  const options: ComboboxOption[] = (users.data ?? []).map((user) => ({
    value: user.id,
    label: userName(user),
    detail: [user.id === me.id ? "you" : null, user.is_active ? null : "inactive"].filter(Boolean).join(", ") || undefined,
  }));
  const asked = tool.queued;
  const whose = (users.data ?? []).find((u) => u.id === asked?.user_id);
  const dayLink =
    asked?.user_id && asked.day
      ? `/?${new URLSearchParams(asked.user_id === me.id ? { date: asked.day } : { user: asked.user_id, date: asked.day }).toString()}`
      : null;

  return (
    <ToolSection
      title="Rebuild a day"
      description="Builds one person's day again from its calls, sessions and other activities, as the nightly rebuild does for the last 7 days. Blocks that are logged, dismissed or edited are kept as they are. The day is that person's own, in their time zone; it starts as today there."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (userId === "") {
            tool.setError("Choose whose day to rebuild.");
            return;
          }
          if (!isIsoDay(day)) {
            tool.setError("Choose the day.");
            return;
          }
          tool.ask({ userId, day });
        }}
      >
        <Combobox
          label="Tech"
          options={options}
          value={userId}
          onChange={(id) => {
            setUserId(id);
            if (!dayPicked) {
              // Today in that person's own time zone, the one their day is in:
              // a day that hasn't begun there yet would be refused.
              const person = (users.data ?? []).find((u) => u.id === id);
              setDay(todayIn(person?.timezone ?? me.timezone));
            }
          }}
          loading={users.isPending}
          failed={users.isError}
          placeholder="Type a name"
          failedText="Couldn't load the people"
        />
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-[5px]">
            <label htmlFor={dayId} className="text-xs font-semibold text-muted">
              Day
            </label>
            <input
              id={dayId}
              type="date"
              className={inputClass}
              value={day}
              onChange={(event) => {
                setDay(event.target.value);
                setDayPicked(true);
              }}
            />
          </div>
          <button type="submit" className={primaryButton} disabled={tool.pending}>
            Rebuild the day
          </button>
        </div>
      </form>
      <Outcome
        queued={asked}
        error={tool.error}
        waiting="That day's rebuild is already waiting to run, so nothing more was queued."
        where={
          dayLink !== null && asked?.day ? (
            <>
              {`Once it has run, ${formatLongDay(asked.day)} shows the rebuilt blocks. `}
              <Link to={dayLink} className={linkClass}>
                {whose ? `Open ${possessive(userName(whose))} day` : "Open that day"}
              </Link>
            </>
          ) : null
        }
      />
    </ToolSection>
  );
}

function PeriodTool({
  title,
  description,
  button,
  run,
  waiting,
  where,
  periods = PERIODS,
  defaultPeriod = DEFAULT_PERIOD,
  option,
}: {
  title: string;
  description: ReactNode;
  button: string;
  run: (hours: number, option: boolean) => Promise<Queued>;
  waiting: string;
  where: ReactNode;
  periods?: readonly ComboboxOption[];
  defaultPeriod?: string;
  /** A checkbox the tool can also take, off to start. */
  option?: { label: string; hint: string };
}) {
  const [hours, setHours] = useState(defaultPeriod);
  const [chosen, setChosen] = useState(false);
  const optionId = useId();
  const hintId = useId();
  const tool = useTool(({ period, also }: { period: number; also: boolean }) => run(period, also));
  return (
    <ToolSection title={title} description={description}>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (hours === "") {
            tool.setError("Choose how far back to look.");
            return;
          }
          tool.ask({ period: Number(hours), also: chosen });
        }}
      >
        <div className="min-w-48 grow basis-48">
          <Combobox label="Period" options={[...periods]} value={hours} onChange={setHours} placeholder="Choose a period" />
        </div>
        {option ? (
          <div className="flex basis-full items-start gap-2">
            <input
              id={optionId}
              type="checkbox"
              className="mt-1 size-4 accent-brand"
              checked={chosen}
              aria-describedby={hintId}
              onChange={(event) => {
                setChosen(event.target.checked);
              }}
            />
            <div className="flex flex-col gap-0.5">
              <label htmlFor={optionId} className="font-medium">
                {option.label}
              </label>
              <span id={hintId} className="text-sm text-muted">
                {option.hint}
              </span>
            </div>
          </div>
        ) : null}
        <button type="submit" className={primaryButton} disabled={tool.pending}>
          {button}
        </button>
      </form>
      <Outcome queued={tool.queued} error={tool.error} waiting={waiting} where={where} />
    </ToolSection>
  );
}

function MissedCalls() {
  const { api } = useSession();
  return (
    <PeriodTool
      title="Missed calls"
      description="Reads SkySwitch's call history for the period again, and gives each call to its tech, as the nightly read does for the previous day: for after mapping an extension, or when calls seem to be missing. SkySwitch keeps about a week of history, so 7 days is the most."
      button="Look for missed calls"
      run={(hours) => api.lookForMissedCalls(hours)}
      waiting="A look for missed calls is already waiting to run, so nothing more was queued."
      where={
        <>
          Once it has run, Health → SkySwitch shows how many calls it read under Last history read, until the
          next 15-minute read replaces it. After that, its counts are in the worker&apos;s log, on its{" "}
          <code className="font-mono text-sm">skyswitch poll</code> line. <HealthLink />
        </>
      }
    />
  );
}

function MissedSessions() {
  const { api } = useSession();
  return (
    <PeriodTool
      title="Missed ScreenConnect sessions"
      description="Reads back from ScreenConnect every session with a connection in the period, and every session a technician joined in it that the app never saw: for after mapping a technician's ScreenConnect name, or when the automation was off. A week reads up to 350 sessions, at the app's own budget of 30 requests a minute (ScreenConnect names no limit): up to about 12 minutes, while ScreenConnect's other jobs wait (each vendor has a lane of its own). A deploy while it reads stops it: run it again once the deploy is done."
      button="Look for missed ScreenConnect sessions"
      run={(hours, unjoined) => api.lookForMissedSessions(hours, unjoined)}
      option={{
        label: "Also sessions nobody joined",
        hint: "For a tech's actions taken without joining that the automation never sent (production before its filter was widened, say). Reads every session with any activity in the period that no technician joined: about 400 a day, so up to about 15 minutes for a day and 30 for a week.",
      }}
      waiting="A look for missed sessions is already waiting to run, so nothing more was queued."
      where={
        <>
          Once it has run, Health → ScreenConnect shows what it read under Last reconciliation, until the next
          15-minute read-back replaces it. After that, its counts are in the worker&apos;s log, on its{" "}
          <code className="font-mono text-sm">screenconnect reconcile</code> line with these hours.{" "}
          <HealthLink />
        </>
      }
    />
  );
}

function MissedDattoActivity() {
  const { api } = useSession();
  return (
    <PeriodTool
      title="Missed Datto RMM activity"
      description="Reads Datto RMM's activity log for the period again: for after mapping a tech or a site, so their work since shows. A week is the most."
      button="Look for missed Datto RMM activity"
      run={(hours) => api.lookForMissedDattoActivity(hours)}
      waiting="A look for missed Datto RMM activity is already waiting to run, so nothing more was queued."
      where={
        <>
          Once it has run, Health → Datto RMM shows it under Activity log, until the next 10-minute read
          replaces it. After that, its counts are in the worker&apos;s log, on its{" "}
          <code className="font-mono text-sm">datto rmm activities applied</code> line. <HealthLink />
        </>
      }
      periods={DATTO_PERIODS}
      defaultPeriod="1"
    />
  );
}

function MissedEmail() {
  const { api } = useSession();
  return (
    <PeriodTool
      title="Missed email"
      description="Reads each switched-on tech's Sent Items for the period again, and writes each email as its notification would have: for after a subscription lapsed, or when email seems to be missing from a day. An email already on a day stays one, and one in a logged block isn't changed. A week is the most."
      button="Look for missed email"
      run={(hours) => api.lookForMissedEmail(hours)}
      waiting="A look for missed email is already waiting to run, so nothing more was queued."
      where={
        <>
          Once it has run, Health → Microsoft 365 shows how many emails it read into days under Looked for missed
          email. Its counts are also in the worker&apos;s log, on its{" "}
          <code className="font-mono text-sm">microsoft graph read back</code> line. <HealthLink />
        </>
      }
    />
  );
}

/** What a by-hand job's button says and does (ADR 0135). */
interface JobText {
  button: string;
  // Its one line: what it does.
  description: string;
  where: ReactNode;
  // Said when the same job, or one under its lock, was already waiting.
  waiting: string;
  // Asked before it runs: it changes a vendor's subscriptions.
  confirm?: string;
}

function LogLine({ children }: { children: string }) {
  return <code className="font-mono text-sm">{children}</code>;
}

// Every job the API lists has its words here: a new one can't type-check
// until it does.
const JOB_TEXT: Record<OwnerJobKey, JobText> = {
  "rebuild-recent-days": {
    button: "Rebuild the last 7 days",
    description:
      "Builds every active person's last 7 days again, as the nightly rebuild does at 07:45 UTC: for after a release changes how blocks are made. Logged, dismissed and edited blocks are kept as they are. It takes a few minutes, on the worker's housekeeping lane.",
    where: "Once it has run, each day shows its rebuilt blocks: reload a day you have open.",
    waiting:
      "A rebuild of recent days is already waiting to run (yours, or the nightly one), so nothing more was queued.",
  },
  "skyswitch-directory": {
    button: "Copy SkySwitch's extensions",
    description:
      "Copies SkySwitch's extension list now, as every night at 07:15 UTC: for a new phone to map on Mappings → Techs.",
    where:
      "Once it has run, Mappings → Techs lists the extensions. Health → SkySwitch shows it under Extension list.",
    waiting: "A copy of the extension list is already waiting to run, so nothing more was queued.",
  },
  "screenconnect-companies": {
    button: "Copy ScreenConnect's company names",
    description:
      "Copies every company name from ScreenConnect's sessions now, as every night at 07:22 UTC: for a new client to map on Mappings → Clients.",
    where: "Once it has run, Health → ScreenConnect shows it under Company list, and Mappings → Clients offers the names.",
    waiting: "A copy of the company names is already waiting to run, so nothing more was queued.",
  },
  "datto-sites": {
    button: "Copy Datto RMM's sites",
    description:
      "Copies Datto RMM's sites now, as every night at 07:20 UTC: for a new site to choose under its client on Mappings → Clients.",
    where: "Once it has run, Health → Datto RMM shows it under Sites.",
    waiting: "A copy of the sites is already waiting to run, so nothing more was queued.",
  },
  "datto-users": {
    button: "Copy Datto RMM's users",
    description:
      "Copies Datto RMM's users now, as every night at 07:21 UTC: for a new tech's Datto RMM user on Mappings → Techs.",
    where: "Once it has run, Health → Datto RMM shows it under Users.",
    waiting: "A copy of the users is already waiting to run, so nothing more was queued.",
  },
  "skyswitch-status": {
    button: "Check SkySwitch's subscriptions",
    description:
      "Reads this environment's SkySwitch subscriptions, the API user's scope and the domain's time zone. It changes nothing.",
    where: (
      <>
        Once it has run, Health → SkySwitch shows the subscriptions under Subscription, and the worker&apos;s log
        has its <LogLine>skyswitch status</LogLine> line.
      </>
    ),
    waiting: "A check of SkySwitch's subscriptions is already waiting to run, so nothing more was queued.",
  },
  "datto-status": {
    button: "Check the Datto RMM sign-in",
    description: "Signs in to Datto RMM now, as every hour at 17 past: for after changing its key.",
    where: "Once it has run, Health → Datto RMM shows it under Sign-in.",
    waiting: "A Datto RMM sign-in is already waiting to run, so nothing more was queued.",
  },
  "msgraph-status": {
    button: "Check the Microsoft 365 sign-in",
    description:
      "Signs the daemon in to Microsoft 365 now, as every hour at 47 past: for after changing its certificate.",
    where: "Once it has run, Health → Microsoft 365 shows it under Sign-in and Certificate expires.",
    waiting: "A Microsoft 365 sign-in is already waiting to run, so nothing more was queued.",
  },
  "skyswitch-subscribe": {
    button: "Subscribe to SkySwitch",
    description:
      "Subscribes this environment to Axon's call records and live calls, unless it already is: once after the first deploy, or after unsubscribing.",
    confirm: "This creates this environment's subscriptions at SkySwitch, so calls arrive here as they happen.",
    where: "Once it has run, Health → SkySwitch lists them under Subscription.",
    waiting:
      "A SkySwitch subscription job (subscribing, unsubscribing or the nightly renewal) is already waiting to run, so nothing more was queued.",
  },
  "skyswitch-unsubscribe": {
    button: "Unsubscribe from SkySwitch",
    description:
      "Deletes this environment's SkySwitch subscriptions and checks none is left: before deleting or rolling back a stack, when its release notes say so.",
    confirm:
      "This deletes this environment's subscriptions at SkySwitch: calls stop arriving here until you subscribe again.",
    where: (
      <>
        Its log ends with{" "}
        <LogLine>skyswitch unsubscribed: none of this environment&apos;s subscriptions remain</LogLine>.
      </>
    ),
    waiting:
      "A SkySwitch subscription job (subscribing, unsubscribing or the nightly renewal) is already waiting to run, so nothing more was queued.",
  },
  "msgraph-subscribe": {
    button: "Subscribe the mailboxes",
    description:
      "Subscribes each switched-on tech's Sent Items now, starting again after unsubscribing. The worker also checks every hour, at 52 past.",
    confirm:
      "This creates subscriptions at Microsoft for each switched-on mailbox, so the email they send arrives here.",
    where: "Once it has run, Health → Microsoft 365 lists each mailbox's subscription.",
    waiting: "Subscribing the mailboxes is already waiting to run, so nothing more was queued.",
  },
  "msgraph-unsubscribe": {
    button: "Unsubscribe the mailboxes",
    description:
      "Deletes every one of this environment's Microsoft 365 subscriptions, and stops making new ones until they're subscribed again: before deleting, rebuilding or rolling back a stack to a release before v0.5.0.",
    confirm:
      "This deletes this environment's subscriptions at Microsoft, and email stops arriving here until you subscribe again.",
    where: (
      <>
        Its log ends with{" "}
        <LogLine>microsoft graph unsubscribed: none of this environment&apos;s subscriptions remain</LogLine>, and
        Health → Microsoft 365 shows none.
      </>
    ),
    waiting: "Unsubscribing the mailboxes is already waiting to run, so nothing more was queued.",
  },
};

// The worker's lanes, in words (ADR 0155).
const LANE_NAMES: Record<string, string> = {
  live: "Live calls",
  tech: "Techs' days and logging",
  email: "Email",
  autotask: "Autotask",
  skyswitch: "SkySwitch",
  screenconnect: "ScreenConnect",
  microsoft365: "Microsoft 365",
  datto: "Datto RMM",
  network: "SonicWall",
  default: "Housekeeping",
};

const laneCell = "py-2 pr-3";

/**
 * What each of the worker's lanes is running, since when, and what waits
 * behind it (the owner, 2026-10-15: a test email waited half an hour behind a
 * backfill, and nothing said so). Polled while the panel is open.
 */
function WorkerLanes() {
  const { api, me } = useSession();
  const lanes = useQuery({
    queryKey: ["owner-tools", "lanes"],
    queryFn: () => api.listLanes(),
    refetchInterval: 10_000,
  });
  if (lanes.isPending) {
    return <p className="text-[13.5px] text-muted">Loading the lanes…</p>;
  }
  if (lanes.isError) {
    return (
      <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
        Couldn&apos;t load the worker&apos;s lanes.
      </p>
    );
  }
  return (
    <table className="w-full text-left text-[13.5px]">
      <caption className="sr-only">The worker&apos;s lanes</caption>
      <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
        <tr>
          <th scope="col" className="pb-2 pr-3">
            Lane
          </th>
          <th scope="col" className="pb-2 pr-3">
            Running
          </th>
          <th scope="col" className="pb-2">
            Waiting
          </th>
        </tr>
      </thead>
      <tbody>
        {lanes.data.map((lane: Lane) => (
          <tr key={lane.lane} className="border-t border-line align-top">
            <th scope="row" className={`${laneCell} font-medium`}>
              {LANE_NAMES[lane.lane] ?? lane.lane}
            </th>
            <td className={laneCell}>
              {lane.running ? (
                <>
                  <code className="font-mono text-[12.5px]">{lane.running.job}</code>
                  {lane.running.since ? `, since ${formatDateTimeIn(lane.running.since, me.timezone)}` : null}
                </>
              ) : (
                <span className="text-muted">Idle</span>
              )}
            </td>
            <td className="py-2">
              {lane.next ? (
                <>
                  {`${String(lane.waiting)} waiting: `}
                  <code className="font-mono text-[12.5px]">{lane.next}</code> next
                </>
              ) : (
                <span className="text-muted">None waiting</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** How a job last ran, in words: procrastinate keeps 30 days (ADR 0114). */
function lastRunText(run: OwnerJobRun | null | undefined, timeZone: string): string {
  if (!run) {
    return "Not run in the last 30 days.";
  }
  const when = run.at ? formatDateTimeIn(run.at, timeZone) : null;
  switch (run.status) {
    case "todo":
      return when ? `Last run: waiting to run since ${when}.` : "Last run: waiting to run.";
    case "doing":
      return when ? `Last run: running since ${when}.` : "Last run: running.";
    case "succeeded":
      return when ? `Last run: done at ${when}.` : "Last run: done.";
    case "failed":
      return `${when ? `Last run: failed at ${when}.` : "Last run: failed."} The worker's log says why.`;
    default:
      return when ? `Last run: stopped at ${when}.` : "Last run: stopped.";
  }
}

/** The worker's by-hand jobs, with how each last ran; polled while one waits or runs. */
function useOwnerJobs() {
  const { api } = useSession();
  return useQuery({
    queryKey: OWNER_JOBS_KEY,
    queryFn: () => api.listOwnerJobs(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((job) => job.last_run?.status === "todo" || job.last_run?.status === "doing")
        ? 15_000
        : false,
  });
}

/** One job: its button, what it does, how it last ran, and what came of a click. */
function JobButton({ jobKey, job }: { jobKey: OwnerJobKey; job: OwnerJob | undefined }) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const text = JOB_TEXT[jobKey];
  const descriptionId = useId();
  const askId = useId();
  const [asking, setAsking] = useState(false);
  // Once answered, how it last ran is read again: it's waiting now.
  const tool = useTool(
    () => api.runOwnerJob(jobKey),
    () => {
      void queryClient.invalidateQueries({ queryKey: OWNER_JOBS_KEY });
    },
  );
  const needsConfirming = text.confirm !== undefined || job?.changes_vendor === true;
  const run = () => {
    setAsking(false);
    tool.ask(undefined);
  };

  return (
    <div role="group" aria-label={text.button} className="flex flex-col gap-1.5 border-t border-line pt-3 first:border-t-0 first:pt-0">
      <div>
        <button
          type="button"
          className={needsConfirming ? secondaryButton : primaryButton}
          aria-describedby={descriptionId}
          disabled={tool.pending || asking}
          onClick={() => {
            if (needsConfirming) {
              setAsking(true);
            } else {
              run();
            }
          }}
        >
          {text.button}
        </button>
      </div>
      <p id={descriptionId} className="text-[13px] text-muted">
        {text.description}
      </p>
      <p className="text-[13px] text-muted">{lastRunText(job?.last_run, me.timezone)}</p>
      {asking ? (
        <div
          role="alertdialog"
          aria-labelledby={`${askId}-title`}
          aria-describedby={`${askId}-text`}
          className="flex flex-col gap-2.5 rounded-row border border-status-needs-client-line bg-status-needs-client-bg px-3 py-2.5"
        >
          <p id={`${askId}-title`} className="text-[13.5px] font-semibold text-status-needs-client-fg">
            {`${text.button}?`}
          </p>
          <p id={`${askId}-text`} className="text-[13.5px] text-status-needs-client-fg">
            {text.confirm ?? "This changes the vendor's subscriptions for this environment."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={primaryButton} onClick={run}>
              {`Yes, ${text.button.charAt(0).toLowerCase()}${text.button.slice(1)}`}
            </button>
            <button
              type="button"
              className={secondaryButton}
              // The safer choice takes focus.
              autoFocus
              onClick={() => {
                setAsking(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      <Outcome queued={tool.queued} error={tool.error} waiting={text.waiting} where={text.where} />
    </div>
  );
}

/** A tool of several by-hand jobs, each with its own button (ADR 0135). */
/** The owner's jobs (ADR 0135) in an integration's panel, each with how it last ran. */
function OwnerJobs({ keys }: { keys: OwnerJobKey[] }) {
  const jobs = useOwnerJobs();
  return (
    <>
      {jobs.isError ? (
        <p className="text-[13px] text-muted">
          How each job last ran couldn&apos;t be loaded. The buttons still work.
        </p>
      ) : null}
      {keys.map((key) => (
        <JobButton key={key} jobKey={key} job={jobs.data?.find((job) => job.key === key)} />
      ))}
    </>
  );
}

/** What the latest request says, in words. */
function backupStateText(state: BackupRequest, timeZone: string): string {
  if (state.requested_at === null) {
    return "No backup has been asked for from here yet.";
  }
  const asked = `${formatDateTimeIn(state.requested_at, timeZone)}${state.requested_by ? ` by ${state.requested_by}` : ""}`;
  if (state.waiting) {
    return state.backups_expected
      ? `Requested at ${asked}. The backup container takes it within a minute, and Health → Backups shows the result.`
      : `Requested at ${asked}: waiting. No backup container runs in this environment, so it waits until one does.`;
  }
  const taken = state.taken_at ? `, and taken by the backup container at ${formatDateTimeIn(state.taken_at, timeZone)}` : "";
  return `Last requested at ${asked}${taken}. Health → Backups shows the result.`;
}

/**
 * Back up now (ADR 0099). The backup container is kept apart from the API
 * (ADR 0015), so this records a request, which the container's scheduler
 * takes within about a minute. Polled while it waits.
 */
function BackUpNow() {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [already, setAlready] = useState(false);
  const state = useQuery({
    queryKey: BACKUP_REQUEST_KEY,
    queryFn: () => api.getBackupRequest(),
    refetchInterval: (query) => (query.state.data?.waiting ? 15_000 : false),
    retry: (failures, e) => !(e instanceof ApiError && e.status < 500) && failures < 2,
  });
  const ask = useMutation({
    mutationFn: () => api.requestBackup(),
    onMutate: () => {
      setError(null);
    },
    onSuccess: ({ requested, ...latest }) => {
      setAlready(!requested);
      queryClient.setQueryData(BACKUP_REQUEST_KEY, latest);
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });

  return (
    <ToolPanel
      title="Backup"
      description="A manual backup of the database to the NAS, as before a deploy by hand: for before a change you might want to undo. The backup container takes it: it alone has the NAS and the encryption key. And the backups, each to download as it is, encrypted, or to check with your private key: the restore drill."
    >
      <div>
        <button
          type="button"
          className={primaryButton}
          disabled={ask.isPending || state.data === undefined}
          onClick={() => {
            ask.mutate();
          }}
        >
          Back up now
        </button>
      </div>
      {error !== null ? (
        <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
          {error}
        </p>
      ) : null}
      {state.data !== undefined ? (
        <p role="status" className="rounded-row bg-panel-2 px-3 py-2.5 text-[13.5px]">
          {already ? "A backup is already requested, so nothing more was asked for. " : null}
          {backupStateText(state.data, me.timezone)}
          {state.data.requested_at !== null ? (
            <>
              {" "}
              <HealthLink />
            </>
          ) : null}
        </p>
      ) : state.isError ? (
        <p className="text-[13.5px] text-muted">The last request couldn&apos;t be loaded. Reload the page to try again.</p>
      ) : null}
      <BackupFiles />
    </ToolPanel>
  );
}

const BACKUP_LIST_KEY = ["owner-tools", "backups"];

const KIND_TEXT: Record<ListedBackup["kind"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  predeploy: "Before a deploy",
  manual: "By hand",
};

const WHERE_TEXT: Record<ListedBackup["where"], string> = {
  nas: "NAS",
  this_host: "This host",
};

/**
 * The backups, on the NAS and kept on the Docker host, as the backup
 * container last listed them, and a download of each (ADR 0206): the
 * encrypted `.age` file as it is, streamed from the backup container through
 * the API, audited. Only the private key can read it, and a download never
 * needs it. A check of one (ADR 0209) takes the key for that check alone.
 */
function BackupFiles() {
  const { api, me } = useSession();
  // One below the panel's own heading.
  const Heading = useContext(ToolHeadingLevel) === 2 ? "h3" : "h4";
  const [error, setError] = useState<string | null>(null);
  // The backup whose check is being asked for (ADR 0209), if any.
  const [checking, setChecking] = useState<ListedBackup | null>(null);
  const list = useQuery({
    queryKey: BACKUP_LIST_KEY,
    queryFn: () => api.listBackups(),
    refetchInterval: 60_000,
    retry: (failures, e) => !(e instanceof ApiError && e.status < 500) && failures < 2,
  });
  const download = useMutation({
    mutationFn: (file: string) => api.downloadBackup(file),
    onMutate: () => {
      setError(null);
    },
    onSuccess: (blob, file) => {
      saveBlob(blob, file);
    },
    onError: (e) => {
      setError(`Not downloaded: ${errorMessage(e)}`);
    },
  });

  if (list.data === undefined) {
    return list.isError ? (
      <p className="text-[13.5px] text-muted">The list of backups couldn&apos;t be loaded. Reload the page to try again.</p>
    ) : null;
  }
  const { data } = list;
  return (
    <section aria-label="The backups" className="flex flex-col gap-2">
      <Heading className="text-[15px] font-semibold">The backups</Heading>
      <p className="text-[13.5px] text-muted">
        {data.read_at === null
          ? "Not listed yet: the backup container lists them after each backup, and every 5 minutes."
          : `As the backup container listed them at ${formatDateTimeIn(data.read_at, me.timezone)}, every 5 minutes. Each is encrypted: only your private key can read it, and a download never needs it. Downloads and checks are audited.`}
      </p>
      {data.nas_error !== null ? (
        <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
          {`The NAS couldn't be listed (${data.nas_error}): only this host's backups are shown.`}
        </p>
      ) : null}
      {!data.downloads ? (
        <p className="text-[13.5px] text-muted">Downloads aren&apos;t available in this environment.</p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
          {error}
        </p>
      ) : null}
      {data.backups.length === 0 ? (
        <p className="text-[13.5px]">None listed.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <thead className="text-xs text-muted">
              <tr>
                <th scope="col" className="py-1 pr-3 font-semibold">When</th>
                <th scope="col" className="py-1 pr-3 font-semibold">Kind</th>
                <th scope="col" className="py-1 pr-3 font-semibold">Size</th>
                <th scope="col" className="py-1 pr-3 font-semibold">Where</th>
                <th scope="col" className="py-1 font-semibold">
                  <span className="sr-only">Download or check</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.backups.map((backup) => (
                <tr key={`${backup.where}:${backup.file}`} className="border-t border-line">
                  <td className="py-1.5 pr-3">{formatDateTimeIn(backup.taken_at, me.timezone)}</td>
                  <td className="py-1.5 pr-3">{KIND_TEXT[backup.kind]}</td>
                  <td className="py-1.5 pr-3">{formatBytes(backup.size_bytes)}</td>
                  <td className="py-1.5 pr-3">{WHERE_TEXT[backup.where]}</td>
                  <td className="py-1.5 text-right">
                    {data.downloads ? (
                      <span className="inline-flex gap-2">
                        <button
                          type="button"
                          className={secondaryButton}
                          aria-label={`Download ${backup.file}`}
                          disabled={download.isPending}
                          onClick={() => {
                            download.mutate(backup.file);
                          }}
                        >
                          {download.isPending && download.variables === backup.file ? "Downloading…" : "Download"}
                        </button>
                        <button
                          type="button"
                          className={secondaryButton}
                          aria-label={`Check ${backup.file}`}
                          onClick={() => {
                            setChecking(backup);
                          }}
                        >
                          Check
                        </button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {checking !== null ? (
        <CheckForm
          // A new form, with nothing typed, for each backup.
          key={`${checking.where}:${checking.file}`}
          backup={checking}
          onClose={() => {
            setChecking(null);
          }}
        />
      ) : null}
      <LatestCheck />
    </section>
  );
}

const BACKUP_CHECK_KEY = ["owner-tools", "backup-check"];

// How each kind of backup is named in a sentence: "the daily backup".
const CHECK_KIND_TEXT: Record<BackupCheck["kind"], string> = {
  daily: "daily",
  weekly: "weekly",
  predeploy: "pre-deploy",
  manual: "manual",
};

/** "the daily backup of Oct 30, 2026, 2:30 AM" */
function whichBackup(kind: BackupCheck["kind"], takenAt: string, timeZone: string): string {
  return `the ${CHECK_KIND_TEXT[kind]} backup of ${formatDateTimeIn(takenAt, timeZone)}`;
}

/** A sentence's end: the words as they are, with a full stop if they had none. */
function sentence(words: string): string {
  return /[.!?]$/.test(words) ? words : `${words}.`;
}

/**
 * The form for one check of a backup (ADR 0209): the owner pastes the age
 * private key for this check alone. It's sent once and forgotten: the field
 * is emptied as the answer comes, and the request kept by no cache. Never in
 * privacy mode, which is for showing the app on a shared screen.
 */
function CheckForm({ backup, onClose }: { backup: ListedBackup; onClose: () => void }) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const privacy = usePrivacy();
  const fieldId = useId();
  const hintId = useId();
  const Heading = useContext(ToolHeadingLevel) === 2 ? "h4" : "h5";
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ask = useMutation({
    mutationFn: (pasted: string) => api.checkBackup(backup.file, pasted),
    // Kept nowhere once it's answered: not even in the query cache.
    gcTime: 0,
    onMutate: () => {
      setError(null);
    },
    onSuccess: (check) => {
      queryClient.setQueryData(BACKUP_CHECK_KEY, check);
      onClose();
    },
    onError: (e) => {
      setError(`Not checked: ${errorMessage(e)}`);
    },
    onSettled: () => {
      setKey("");
      ask.reset();
    },
  });
  const where = backup.where === "nas" ? "on the NAS" : "kept on this host";

  return (
    <section aria-label="Check a backup" className="flex flex-col gap-2 rounded-row bg-panel-2 px-3 py-2.5">
      <Heading className="text-[14px] font-semibold">Check a backup</Heading>
      <p className="text-[13.5px]">
        {`Checks ${whichBackup(backup.kind, backup.taken_at, me.timezone)}, ${where}: restored into a scratch database with your key, checked, and dropped. Nothing live is touched.`}
      </p>
      {privacy ? (
        <p className="text-[13.5px] text-status-needs-client-fg">
          Privacy mode is on: turn it off to check a backup. Its key would be pasted on a screen you may be sharing.
        </p>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            ask.mutate(key);
          }}
        >
          <label htmlFor={fieldId} className="text-[13.5px] font-semibold">
            Your age private key
          </label>
          <input
            id={fieldId}
            type="password"
            autoComplete="off"
            spellCheck={false}
            aria-describedby={hintId}
            className={inputClass}
            value={key}
            onChange={(event) => {
              setKey(event.target.value);
            }}
          />
          <p id={hintId} className="text-[13px] text-muted">
            Paste the whole of your backup key file (axon-time-backup-key.txt), or its AGE-SECRET-KEY-1… line. It goes
            to the backup container for this check alone: it&apos;s never saved or logged, and it&apos;s deleted once
            the check ends.
          </p>
          {error !== null ? (
            <p role="alert" className="text-[13.5px] text-status-needs-client-fg">
              {error}
            </p>
          ) : null}
          <div className="flex gap-2">
            <button type="submit" className={primaryButton} disabled={ask.isPending || key.trim() === ""}>
              Check it
            </button>
            <button type="button" className={secondaryButton} onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {privacy ? (
        <div>
          <button type="button" className={secondaryButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** What the latest check is doing, or found, in one sentence. */
function checkStatusText(check: BackupCheck, timeZone: string): string {
  const which = whichBackup(check.kind, check.taken_at, timeZone);
  switch (check.status) {
    case "waiting":
      return `Checking ${which}: waiting for the backup container to take it.`;
    case "running":
      return check.decrypted === true
        ? `Checking ${which}: it decrypted with your key, and is being restored into a scratch database.`
        : `Checking ${which}: decrypting it with your key.`;
    case "passed": {
      const when = formatDateTimeIn(check.finished_at ?? check.requested_at, timeZone);
      const by = check.requested_by ? `, asked for by ${check.requested_by}` : "";
      return `The ${which.slice("the ".length)} checked out: checked at ${when}${by}.`;
    }
    case "failed":
      return `The check of ${which} failed: ${sentence(check.error ?? "the backup container's Logs say why")}`;
    case "refused":
    case "expired":
      return `The check of ${which} wasn't run: ${sentence(check.error ?? "it was refused")}`;
  }
}

/** "4 users", "1 user": with the thousands marked. */
function counted(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** "a, b and c" */
function listed(parts: string[]): string {
  return parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1) ?? ""}`;
}

/** What a finished check found, a line each. */
function checkFindings(check: BackupCheck, timeZone: string): string[] {
  const found: string[] = [];
  if (check.decrypted === true) {
    found.push("It decrypted with your key.");
  } else if (check.decrypted === false) {
    found.push("It didn't decrypt with that key.");
  }
  if (check.restored === true) {
    found.push("It restored into a scratch database, which was then dropped: nothing live was touched.");
  } else if (check.restored === false) {
    found.push("It didn't restore: its scratch database was dropped, and nothing live was touched.");
  }
  if (check.schema_version !== null) {
    found.push(
      check.live_schema_version !== null
        ? `Its schema version is ${check.schema_version}; the live database's is ${check.live_schema_version}.`
        : `Its schema version is ${check.schema_version}.`,
    );
  }
  if (check.restored === true) {
    const newest =
      check.newest_activity_at !== null
        ? `, and its newest activity is from ${formatDateTimeIn(check.newest_activity_at, timeZone)}`
        : "";
    found.push(`It was taken ${formatDateTimeIn(check.taken_at, timeZone)}${newest}.`);
  }
  const counts = check.counts;
  if (counts) {
    const held = [
      counts.users != null ? counted(counts.users, "user", "users") : null,
      counts.activities != null ? counted(counts.activities, "activity", "activities") : null,
      counts.work_blocks != null ? counted(counts.work_blocks, "block", "blocks") : null,
      counts.audit_log_entries != null
        ? counted(counts.audit_log_entries, "audit log entry", "audit log entries")
        : null,
    ].filter((part): part is string => part !== null);
    if (held.length > 0) {
      found.push(`It holds ${listed(held)}.`);
    }
  }
  return found;
}

/**
 * The latest check of a backup (ADR 0209), followed every 2 seconds while it
 * waits or runs: what it found, and for one that checked out, the console
 * steps to replace the live database with it. The app never does that itself.
 */
function LatestCheck() {
  const { api, me } = useSession();
  const latest = useQuery({
    queryKey: BACKUP_CHECK_KEY,
    queryFn: () => api.getBackupCheck(),
    refetchInterval: (query) =>
      query.state.data?.status === "waiting" || query.state.data?.status === "running" ? 2_000 : false,
    retry: (failures, e) => !(e instanceof ApiError && e.status < 500) && failures < 2,
  });
  const check = latest.data;
  if (check === undefined || check === null) {
    return null;
  }
  const finished = check.status === "passed" || check.status === "failed";
  const findings = finished ? checkFindings(check, me.timezone) : [];
  return (
    <div className="flex flex-col gap-2">
      <p role="status" aria-label="The latest check" className="rounded-row bg-panel-2 px-3 py-2.5 text-[13.5px]">
        {checkStatusText(check, me.timezone)}
      </p>
      {findings.length > 0 ? (
        <ul aria-label="What the check found" className="ml-5 list-disc text-[13.5px]">
          {findings.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {check.status === "passed" ? <ReplaceSteps check={check} /> : null}
    </div>
  );
}

/** One console command, with a button that copies it. */
function Command({ text }: { text: string }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <code className="rounded-input border border-line bg-card px-2 py-1 font-mono text-[13px] break-all">{text}</code>
      <button
        type="button"
        className={secondaryButton}
        aria-label={`Copy ${text}`}
        onClick={() => {
          let written: Promise<void> | undefined;
          try {
            written = (navigator as { clipboard?: Clipboard }).clipboard?.writeText(text);
          } catch {
            written = undefined;
          }
          if (written === undefined) {
            setCopied(false);
            return;
          }
          written.then(
            () => {
              setCopied(true);
            },
            () => {
              setCopied(false);
            },
          );
        }}
      >
        Copy
      </button>
      <span aria-live="polite" className="text-[13px] text-muted">
        {copied === true ? "Copied." : copied === false ? "Couldn't copy: select it and copy it by hand." : ""}
      </span>
    </span>
  );
}

/**
 * Replacing the live database with a backup that checked out (ADR 0209): the
 * console steps of backups.md section 6, with this backup's path. Option (a):
 * the app shows them and never runs them.
 */
function ReplaceSteps({ check }: { check: BackupCheck }) {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const stepsId = useId();
  return (
    <div className="flex flex-col gap-2">
      <div>
        <button
          type="button"
          className={secondaryButton}
          aria-expanded={open}
          aria-controls={open ? stepsId : undefined}
          onClick={() => {
            setOpen(!open);
          }}
        >
          Replace the live database with this backup
        </button>
      </div>
      {open ? (
        <section
          id={stepsId}
          aria-label="Replace the live database with this backup"
          className="flex flex-col gap-2 text-[13.5px]"
        >
          <p role="note" className="rounded-row border border-status-needs-client-line px-3 py-2.5 text-status-needs-client-fg">
            {`Anything written after this backup was taken, ${formatDateTimeIn(check.taken_at, me.timezone)}, is lost. Do this only if the live database is lost or damaged.`}
          </p>
          <p>
            The app never does this itself: these are console steps, in Portainer and the backup container&apos;s
            Console, as Backups and restore, section 6, sets out.
          </p>
          <ol className="ml-5 flex list-decimal flex-col gap-2">
            <li>
              In Portainer, stop api, web, worker and syslog in this stack. Leave db and backup running. The worker can
              take up to 2 minutes to stop.
            </li>
            <li className="flex flex-col gap-1">
              <span>In this stack&apos;s backup container&apos;s Console, put your private key where the restore reads it:</span>
              <Command text="cat > /tmp/age-identity.txt" />
              <span>Paste the whole key file, press Enter, then Ctrl-D. That folder is memory only.</span>
            </li>
            <li className="flex flex-col gap-1">
              <span>Restore this backup over the live database:</span>
              <Command text={`restore-backup ${check.path} axon_time --replace-live`} />
              <span>
                It checks the backup again, restores it beside the live database, then swaps it in. The old one is
                kept: its last line names it.
              </span>
            </li>
            <li className="flex flex-col gap-1">
              <span>Remove the key:</span>
              <Command text="rm /tmp/age-identity.txt" />
            </li>
            <li>
              In Portainer, Pull and redeploy the stack, as for a release: migrate brings the restored database up to
              this release.
            </li>
            <li className="flex flex-col gap-1">
              <span>Sign in and check the data. Once you&apos;re satisfied, drop the old database, as step 3&apos;s last line names it:</span>
              <Command text="restore-backup --drop-replaced axon_time_replaced_<time>" />
            </li>
          </ol>
          <p>
            Calls, sessions and Autotask from after the backup can be read again with the tools on this page:
            Backups and restore, section 6, says which.
          </p>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Every tool, each one row until opened (#512; ADR 0134): on the page in
 * Health's grid, two columns from 1100 pixels; in the popup, one column.
 */
export function OwnerTools({ inPopup = false }: { inPopup?: boolean }) {
  return (
    <ToolHeadingLevel.Provider value={inPopup ? 3 : 2}>
      <div className={inPopup ? "flex flex-col gap-3" : "grid items-start gap-3 min-[1100px]:grid-cols-2"}>
        <ToolPanel
          title="Worker"
          description="What each of the worker's lanes is running now, and what waits behind it. Each lane runs one job at a time: email, live calls, techs' days and each vendor have their own, so a vendor's backfill holds up only that vendor's jobs. Refreshed every 10 seconds while open."
        >
          <WorkerLanes />
        </ToolPanel>
        <ToolPanel title="Autotask" description="What the app copies from Autotask every night, now.">
          <AutotaskSync />
        </ToolPanel>
        <ToolPanel
          title="SkySwitch"
          description="Calls: SkySwitch's history read again, its extension list copied, and how it sends this environment its calls."
        >
          <MissedCalls />
          <OwnerJobs keys={["skyswitch-directory", "skyswitch-status", "skyswitch-subscribe", "skyswitch-unsubscribe"]} />
        </ToolPanel>
        <ToolPanel
          title="ScreenConnect"
          description="Sessions: read back from ScreenConnect, and its company names copied."
        >
          <MissedSessions />
          <OwnerJobs keys={["screenconnect-companies"]} />
        </ToolPanel>
        <ToolPanel
          title="Datto RMM"
          description="Remote work: Datto RMM's activity log read again, its sites and users copied, and its sign-in checked."
        >
          <MissedDattoActivity />
          <OwnerJobs keys={["datto-sites", "datto-users", "datto-status"]} />
        </ToolPanel>
        <ToolPanel
          title="Microsoft 365"
          description="Email: each tech's Sent Items read again, the sign-in checked, Axon Time's own emails sent to you, and how Microsoft sends this environment the email they send."
        >
          <MissedEmail />
          <SendEmail />
          <OwnerJobs keys={["msgraph-status", "msgraph-subscribe", "msgraph-unsubscribe"]} />
        </ToolPanel>
        <ToolPanel title="Days" description="The day view's blocks, built again from their activities.">
          <RebuildDay />
          <OwnerJobs keys={["rebuild-recent-days"]} />
        </ToolPanel>
        <BackUpNow />
        {/* Moved from Health: a tool, not a status (ADR 0198). */}
        <ToolPanel
          title="Captures"
          description="A sanitized sample of what a vendor sends, recorded over a window you choose, to download and commit as test fixtures (ADR 0119). Each source's captures are listed under it."
        >
          <CaptureTools />
        </ToolPanel>
      </div>
    </ToolHeadingLevel.Provider>
  );
}

export function OwnerToolsPage() {
  return (
    <>
      <Panel>
        <h1 className="text-xl font-semibold tracking-tight">Owner tools</h1>
        <p className="mt-1 text-muted">
          The worker&apos;s jobs, started now instead of at their next scheduled time. Press{" "}
          <kbd className="rounded-input border border-line bg-card px-1.5 font-mono text-sm">`</kbd> (the
          backtick key, left of 1) anywhere in the app, outside a text field, or choose Owner tools in the user
          menu (your avatar, top right), under the theme, and they open over the page you&apos;re on; the key
          again, Escape or a click outside closes them. Here, at{" "}
          <code className="font-mono text-sm">/tools</code>, they&apos;re a page of their own. Only owners can
          open them.
        </p>
      </Panel>
      <OwnerTools />
    </>
  );
}
